/**
 * Pen / Pin / Mood notes of the My Studio timeline.
 *
 * They still travel on the legacy chat channel (`/projects/{id}/messages`,
 * type TEAM) with the note data in `extra` (PenNoteExtra / PinNoteExtra /
 * MoodNoteExtra). Moving them onto `/communications` is API_GAPS_FOR_BE.md §9;
 * until then this module is the single mapping between the timeline
 * submissions and that payload.
 */

export const NOTE_MODES = ['penai', 'pinai', 'moodai'];

export const MODE_TO_NOTE_TYPE = { penai: 'pen', pinai: 'pin', moodai: 'mood' };

const NOTE_TYPE_TO_MODE = { pen: 'penai', pin: 'pinai', mood: 'moodai' };

/** Every layer `NoteLayer` accepts; anything else answers 422. */
const API_LAYERS = ['MUSIC', 'SFX', 'MIX'];

/**
 * The timeline layer as the API can store it. REFERENCE takes the MUSIC row in
 * reference mode, so its notes are stored on MUSIC; layers the enum does not
 * know (rec session collaborators) become "the whole timeline".
 */
export const toApiLayer = (layer) => {
    if (layer === 'REFERENCE') return 'MUSIC';
    return API_LAYERS.includes(layer) ? layer : null;
};

/** `extra` for POST /projects/{id}/messages. */
export const buildNoteExtra = (mode, { layer, startPos, endPos, mood, canvasJson }) => {
    const extra = {
        noteType: MODE_TO_NOTE_TYPE[mode],
        layer: toApiLayer(layer),
        startTimecode: startPos ?? null,
        endTimecode: endPos ?? null,
    };
    if (mode === 'penai') extra.canvasData = JSON.stringify(canvasJson ?? null);
    if (mode === 'moodai') extra.mood = mood ?? null;
    return extra;
};

const parseCanvas = (canvasData) => {
    if (!canvasData) return null;
    try {
        return JSON.parse(canvasData);
    } catch {
        return null;
    }
};

/** A saved message as a timeline submission, or null when it is not a note. */
export const messageToSubmission = (message) => {
    const extra = message?.extra;
    const mode = NOTE_TYPE_TO_MODE[extra?.noteType];
    if (!mode) return null;

    return {
        id: message.id,
        mode,
        note: message.body,
        mood: extra.mood ?? null,
        layer: extra.layer ?? null,
        startPos: extra.startTimecode ?? null,
        endPos: extra.endTimecode ?? null,
        canvasJson: mode === 'penai' ? parseCanvas(extra.canvasData) : null,
        timelineType: 'studio',
        createdAt: message.createdAt,
    };
};

export const isLocalSubmission = (submission) => String(submission?.id).startsWith('local_');
